import { AdEventKey, SeekResultType, PlaybackState, } from '..';
export class SeekController {
    channelInfo;
    adsInfo;
    unwatchedAds;
    visitedAds = null;
    lastUpdatedCurrentTime = null;
    adBreakSkipEmitted = false;
    adBreakEnd = undefined;
    // Because we are not adding GT12 ski via playerevents in native adapter,
    // we directly trigger the event here via player.
    triggerSkipAdbreakEvent = null;
    constructor(channelInfo, adsInfo, triggerSkipAdbreakEvent) {
        this.channelInfo = channelInfo;
        this.adsInfo = adsInfo;
        this.unwatchedAds = channelInfo.schedule.flatMap((ad) => ad.markers);
        this.triggerSkipAdbreakEvent = triggerSkipAdbreakEvent;
    }
    canSeek(from, to, playbackState) {
        return this.evaluate(from, to, playbackState);
    }
    emitSkipAdBreakEvent(withinAdBreak, end) {
        if ((this.adBreakSkipEmitted && withinAdBreak) ||
            (!this.adBreakSkipEmitted && !withinAdBreak)) {
            return;
        }
        if (!this.adBreakSkipEmitted && withinAdBreak) {
            this.adBreakSkipEmitted = true;
            this.adBreakEnd = end;
            this.triggerSkipAdbreakEvent?.(AdEventKey.AD_BREAK_SKIP_AVAILABLE);
            return;
        }
        if (this.adBreakSkipEmitted && !withinAdBreak) {
            this.adBreakSkipEmitted = false;
            this.adBreakEnd = undefined;
            this.triggerSkipAdbreakEvent?.(AdEventKey.AD_BREAK_SKIP_UNAVAILABLE);
        }
    }
    getAdBreakEnd() {
        return this.adBreakEnd;
    }
    update(currentTime, playbackState) {
        if (playbackState !== PlaybackState.PLAYING) {
            return;
        }
        if (!this.lastUpdatedCurrentTime) {
            this.lastUpdatedCurrentTime = currentTime;
            return;
        }
        const adBreak = this.adBreakAtTime(currentTime);
        if (adBreak !== null) {
            this.visitedAds = adBreak;
            this.lastUpdatedCurrentTime = Math.max(this.lastUpdatedCurrentTime, currentTime);
        }
        if (this.channelInfo.adBreakSkipAllowed) {
            this.emitSkipAdBreakEvent(Boolean(adBreak), adBreak?.end);
        }
    }
    canFastForward(currentTime, playbackState) {
        return this.evaluate(currentTime, currentTime, playbackState);
    }
    evaluateSeekGT12BV(currentTime, destinationTime, playbackState) {
        const adBreak = this.adBreakAtTime(currentTime);
        if (playbackState === PlaybackState.PLAYING &&
            adBreak) {
            this.visitedAds = adBreak;
        }
        const visitedAds = this.visitedAds;
        if (visitedAds && destinationTime >= visitedAds.start) {
            return this.seekAndDisplayAd(Math.max(currentTime, this.lastUpdatedCurrentTime ?? 0), destinationTime, visitedAds);
        }
        return {
            type: SeekResultType.GT12_ALLOWED,
            seekPosition: destinationTime,
        };
    }
    seekAndDisplayAd(currentTime, destinationTime, adBreak) {
        const bcff = this.adsInfo?.fastForward;
        if (!bcff) {
            return {
                type: SeekResultType.GT12_ALLOWED,
                seekPosition: destinationTime,
            };
        }
        let nextDestinationTime = destinationTime;
        const nextAdBreak = this.adBreakAtTime(destinationTime);
        if (nextAdBreak === adBreak) {
            nextDestinationTime = adBreak.end;
        }
        let timeLeft = adBreak.end - currentTime;
        if (bcff.maxDuration) {
            timeLeft = Math.max(0, Math.min(timeLeft, bcff.maxDuration));
        }
        let vastUrl = bcff.vastUrl;
        const requestedMaxDuration = Math.ceil(timeLeft);
        vastUrl += `&Maxduration=${requestedMaxDuration}`;
        this.unwatchedAds = this.unwatchedAds.filter((ad) => ad !== adBreak);
        this.visitedAds = null;
        return {
            type: SeekResultType.GT12_SEEK_TO_AND_DISPLAY_ADBREAK,
            seekPosition: nextDestinationTime,
            vastUrl,
            isChannelAd: bcff.isChannelAd,
            maxDuration: requestedMaxDuration,
        };
    }
    evaluate(currentTime, destinationTime, playbackState) {
        if (!this.channelInfo.forwardSeekingNeedsMarkers) {
            return this.evaluateSeekGT12BV(currentTime, destinationTime, playbackState);
        }
        if (!this.channelInfo.isGT12BV) {
            return {
                type: SeekResultType.ALLOWED,
                seekPosition: destinationTime,
            };
        }
        if (this.channelInfo.schedule.length === 0) {
            return {
                type: SeekResultType.GT12_FORBIDDEN,
            };
        }
        const currentAd = this.adBreakAtTime(currentTime);
        if (currentAd) {
            if (destinationTime < currentTime) {
                const previousAdBreak = this.adBreakAtTime(destinationTime);
                if (previousAdBreak && previousAdBreak !== currentAd) {
                    return {
                        type: SeekResultType.GT12_SEEK_TO_ADBREAK,
                        seekPosition: previousAdBreak.start,
                        marker: previousAdBreak,
                    };
                }
                else {
                    return {
                        type: SeekResultType.GT12_ALLOWED,
                        seekPosition: destinationTime,
                    };
                }
            }
            else {
                return {
                    type: SeekResultType.GT12_FORBIDDEN,
                };
            }
        }
        const marker = this.firstAdBreak(currentTime, destinationTime);
        if (marker) {
            return {
                type: SeekResultType.GT12_SEEK_TO_ADBREAK,
                seekPosition: marker.start,
                marker,
            };
        }
        return {
            type: SeekResultType.GT12_ALLOWED,
            seekPosition: destinationTime,
        };
    }
    firstAdBreak(currentTime, destinationTime) {
        return this.unwatchedAds.find((ad) => {
            return ad.start < destinationTime && ad.end > currentTime;
        }) ?? null;
    }
    adBreakAtTime(currentTime) {
        return this.unwatchedAds.find((ad) => {
            return currentTime >= ad.start && currentTime < ad.end;
        }) ?? null;
    }
}
